import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  BadgeCheck,
  Bookmark,
  BriefcaseBusiness,
  Building2,
  Check,
  CircleDollarSign,
  LockKeyhole,
  Search,
  Send,
  Sparkles,
  UserRound,
  X,
} from 'lucide-react';

const tabs = ['For You', 'Ventures', 'People', 'Angels', 'Articles', 'Messages', 'Profile'];

const intentItems = [
  { label: 'Find cofounder', icon: UserRound },
  { label: 'Find talent', icon: UserRound },
  { label: 'Find work', icon: BriefcaseBusiness },
  { label: 'Find funding', icon: CircleDollarSign },
  { label: 'Invest in ventures', icon: Building2 },
];

const intentCopy = {
  'Find cofounder': 'Founder-level gaps, complementary Arc signals, and people open to serious cofounder conversations.',
  'Find talent': 'Skilled people who are open to work, roles, projects, or cofounder-level conversations.',
  'Find work': 'Ventures and businesses looking for talent, with clear needs and founder context.',
  'Find funding': 'Angels and capital sources available through structured requests, not open inbox noise.',
  'Invest in ventures': 'Ventures and businesses currently raising or open to investment, with owner-controlled detail pages.',
};

const arcDescriptions = {
  Builder: [
    'Turns unclear work into a practical execution plan.',
    'Strong when teams need momentum, ownership, and follow through.',
    'Best used where progress depends on steady delivery.',
  ],
  Composer: [
    'Brings structure, taste, and rhythm to creative work.',
    'Strong at turning messy ideas into usable systems or experiences.',
    'Best used where clarity and polish need to move together.',
  ],
  Engineer: [
    'Builds fast by reducing problems to what can be tested.',
    'Strong at prototypes, systems, and practical technical decisions.',
    'Best used where ideas need to become working products.',
  ],
  Kingmaker: [
    'Spots talent, leverage, and the people needed around a mission.',
    'Strong at positioning others where they can create outsized impact.',
    'Best used where power, capital, and team design matter.',
  ],
  Navigator: [
    'Reads complex situations and helps people choose the next move.',
    'Strong at risk, context, and decision clarity under pressure.',
    'Best used where the path is unclear and timing matters.',
  ],
  Rainmaker: [
    'Creates momentum through deals, pressure, and commercial direction.',
    'Strong at opening doors, closing loops, and moving markets.',
    'Best used where growth depends on sharper external traction.',
  ],
  Weaver: [
    'Connects people, systems, and ideas that usually stay separate.',
    'Strong at integration, product structure, and cross-team clarity.',
    'Best used where separate parts need to work as one system.',
  ],
};

const ventures = [
  {
    id: 'sterling',
    type: 'venture',
    title: 'Sterling Syndicate Signals',
    summary: 'Real time market intelligence and execution signals for informed investors.',
    problem: 'Private investors struggle to separate signal quality from market noise.',
    solution: 'A decision layer that pairs market intelligence with execution review and risk context.',
    stage: 'Growth Stage',
    sector: 'Fintech',
    location: 'London, UK',
    capital: 'Strategic angel round open',
    gapSignal: 'Needs trusted distribution and compliance depth before broader investor rollout.',
    matchSignal: 'Capital ready',
    dataRoom: 'Founder memo, progress snapshot, compliance notes, angel briefing',
    needs: ['Strategic partners', 'Growth capital', 'Compliance review'],
    traction: ['1,800 active watchlists', 'Revenue positive', 'Private beta with 4 angel groups'],
    qualitySignals: ['Revenue positive private beta', 'Investor audience already engaged', 'Compliance review listed as an explicit need'],
    openRoles: ['Strategic finance advisor', 'Compliance partner', 'Investor relations lead'],
    founder: 'Sebastian Valdez',
    founderVerified: true,
    founderArc: 'Navigator',
    founderEngine: 'ORACLE',
    founderRole: 'Founder and CEO',
    founderBio: 'Former market analyst building a more disciplined signal product.',
    surfaced: ['Your intent includes funding and investor grade ventures.', 'The venture has a clear capital ask and measurable traction.'],
    imageTone: 'markets',
    intents: ['Find funding', 'Invest in ventures'],
  },
  {
    id: 'nexora',
    type: 'venture',
    title: 'Nexora',
    summary: 'Mission driven infrastructure company building the future of clean energy.',
    problem: 'Clean energy projects lose momentum when capital, regulation, and field execution move separately.',
    solution: 'A project infrastructure layer that helps teams coordinate capital, policy, and delivery.',
    stage: 'Series A',
    sector: 'Climate Tech',
    location: 'Zurich, Switzerland',
    capital: 'Series A materials ready',
    gapSignal: 'Scale pressure is shifting from vision to operations, policy, and capital sequencing.',
    matchSignal: 'Scale gap',
    dataRoom: 'Pilot metrics, utility partner notes, round memo, operations map',
    needs: ['Growth capital', 'Operations talent', 'Policy and regulatory'],
    traction: ['3 active pilots', 'Two utility partnerships', 'Series A materials ready'],
    qualitySignals: ['Active pilots in motion', 'Utility partnerships secured', 'Clear operational gap before expansion'],
    openRoles: ['Operations lead', 'Policy advisor', 'Capital strategy partner'],
    founder: 'Alex Morgan',
    founderVerified: true,
    founderArc: 'Kingmaker',
    founderEngine: 'TITAN',
    founderRole: 'Founder and CEO',
    founderBio: 'Former energy strategist with a passion for building systems that last.',
    surfaced: ['This venture has strong execution needs and a founder Arc that can absorb scale pressure.', 'The current ask is specific enough for a useful conversation.'],
    imageTone: 'mountain',
    intents: ['Find work', 'Find funding', 'Invest in ventures'],
  },
  {
    id: 'almatek',
    type: 'venture',
    title: 'AlmaTek AI',
    summary: 'Developer friendly AI tools to ship smarter products faster.',
    problem: 'Small product teams lose time switching between learning, debugging, and shipping.',
    solution: 'An AI build companion tuned for practical product decisions and clean implementation support.',
    stage: 'Seed Stage',
    sector: 'AI / SaaS',
    location: 'Berlin, Germany',
    capital: 'Seed capital and design partners',
    gapSignal: 'Strong technical base needs product judgment and early customer conversion.',
    matchSignal: 'Builder needed',
    dataRoom: 'Prototype walkthrough, waitlist quality, design partner notes',
    needs: ['Engineering talent', 'Seed capital', 'Design partner'],
    traction: ['1,200 waitlist signups', 'Prototype shipped', 'Three design partners'],
    qualitySignals: ['Prototype already shipped', 'Design partners identified', 'Technical founder with product gap made explicit'],
    openRoles: ['Product engineer', 'Founding designer', 'Seed angel'],
    founder: 'Maksym D.',
    founderVerified: true,
    founderArc: 'Engineer',
    founderEngine: 'ALCHEMIST',
    founderRole: 'Founder and CEO',
    founderBio: 'Technical founder focused on reducing build friction for practical teams.',
    surfaced: ['Your intent overlaps with technical talent and early stage venture discovery.', 'The venture needs builders who can turn prototype momentum into product quality.'],
    imageTone: 'objects',
    intents: ['Find work', 'Invest in ventures'],
  },
  {
    id: 'heliogrid',
    type: 'venture',
    title: 'HelioGrid',
    summary: 'A field operations platform helping solar installers coordinate crews, materials, and customer updates.',
    problem: 'Solar installers run high value work with scattered field notes, spreadsheets, and delayed updates.',
    solution: 'A calm operating layer for crew scheduling, material readiness, and customer progress visibility.',
    stage: 'Pilot Stage',
    sector: 'Energy Ops',
    location: 'Melbourne, Australia',
    capital: 'Pilot partners and go to market support',
    gapSignal: 'Clear field problem with early pilots, now needs product and distribution discipline.',
    matchSignal: 'Pilot ready',
    dataRoom: 'Pilot notes, customer workflow map, crew scheduling prototype',
    needs: ['Product talent', 'Pilot partners', 'Go to market support'],
    traction: ['Two paid pilots', 'Crew scheduling prototype', 'Installer advisory group'],
    qualitySignals: ['Two paid pilots', 'Specific field workflow pain', 'Founder has direct operations background'],
    openRoles: ['Product builder', 'Installer partnerships', 'Customer success lead'],
    founder: 'Priya Shah',
    founderVerified: true,
    founderArc: 'Builder',
    founderEngine: 'TITAN',
    founderRole: 'Founder and CEO',
    founderBio: 'Former field operations lead building calmer systems for energy teams.',
    surfaced: ['This venture has a clear operational problem and a practical founder profile.', 'It is a good match for product talent and early pilot partners.'],
    imageTone: 'grid',
    intents: ['Find work'],
  },
];

const people = [
  {
    id: 'maya',
    type: 'person',
    name: 'Maya Chen',
    arc: 'Composer',
    engine: 'ARTISAN',
    title: 'Product Designer',
    summary: 'UX and product designer crafting intuitive experiences.',
    intent: 'Open to product roles and cofounder conversations',
    availability: 'Part time advisory',
    networkAsk: 'Looking for ventures that need product direction before hiring a full design team.',
    credibility: 'Portfolio reviewed',
    verified: true,
    location: 'London, UK',
    tags: ['Product Design', 'UX/UI', 'Design Systems'],
    strengths: ['Turns messy ideas into usable screens', 'Strong customer empathy', 'Premium interface taste'],
    surfaced: ['Useful for ventures that need product clarity before hiring a full team.', 'Strong match for founders who need product taste and execution rhythm.'],
    tone: 'portrait-one',
    intents: ['Find cofounder', 'Find talent'],
  },
  {
    id: 'daniel',
    type: 'person',
    name: 'Daniel Kim',
    arc: 'Rainmaker',
    engine: 'TITAN',
    title: 'Growth and GTM',
    summary: 'Growth strategist helping teams scale with clarity and focus.',
    intent: 'Open to advisory and venture growth roles',
    availability: 'Selective projects',
    networkAsk: 'Looking for ventures with early product pull but weak commercial motion.',
    credibility: 'Founder references ready',
    verified: true,
    location: 'Toronto, Canada',
    tags: ['Growth Strategy', 'GTM', 'Partnerships'],
    strengths: ['Clarifies market motion', 'Builds early partnerships', 'Strong commercial pressure'],
    surfaced: ['Useful for ventures with product momentum but weak distribution.', 'Strong match for founders who need sharper market pressure.'],
    tone: 'portrait-two',
    intents: ['Find talent'],
  },
  {
    id: 'isabella',
    type: 'person',
    name: 'Isabella Rossi',
    arc: 'Weaver',
    engine: 'ALCHEMIST',
    title: 'Product Designer',
    summary: 'Product thinker and designer with a systems mindset.',
    intent: 'Looking for early product teams',
    availability: 'Available now',
    networkAsk: 'Looking for an early team where research and product structure matter.',
    credibility: 'Open to verification',
    verified: false,
    location: 'Milan, Italy',
    tags: ['Product Design', 'Research', 'Systems'],
    strengths: ['Maps user journeys', 'Builds structured design systems', 'Connects research to product decisions'],
    surfaced: ['Useful for ventures that need better product architecture.', 'Strong match for founders who need thinking and execution in the same person.'],
    tone: 'portrait-three',
    intents: ['Find talent'],
  },
  {
    id: 'marcus',
    type: 'person',
    name: 'Marcus Hale',
    arc: 'Navigator',
    engine: 'ORACLE',
    title: 'Angel Investor',
    summary: 'Early stage investor focused on software and climate.',
    intent: 'Looking for disciplined founders',
    availability: 'Request only',
    networkAsk: 'Looking for focused founders with traction, a clean ask, and a strong operating signal.',
    credibility: 'MYARC angel review complete',
    verified: true,
    location: 'Zurich, Switzerland',
    tags: ['Early Stage', 'SaaS', 'Climate'],
    strengths: ['Pattern recognition', 'Calm capital judgment', 'Strategic access'],
    surfaced: ['Relevant for ventures needing early capital and market judgment.', 'Best contacted with a structured request, not an open message.'],
    tone: 'portrait-four',
    intents: [],
  },
];

const angels = [
  {
    id: 'sophia-grant',
    type: 'angel',
    name: 'Sophia Grant',
    title: 'Angel Investor',
    arc: 'Navigator',
    engine: 'ORACLE',
    summary: 'Backs practical software, finance education, and decision tools.',
    sectors: ['Fintech', 'Education', 'SaaS'],
    stages: ['Pre seed', 'Seed'],
    checkSize: '$25k to $100k',
    thesis: 'Practical decision tools, finance education, and software that improves judgment.',
    style: 'Hands on strategic access',
    access: 'Structured request required',
    verified: true,
    angel: true,
    tone: 'portrait-three',
    surfaced: ['Relevant for finance and education products.', 'Requires clear venture context before chat opens.'],
  },
  {
    id: 'marcus-hale',
    type: 'angel',
    name: 'Marcus Hale',
    title: 'Climate and SaaS Angel',
    arc: 'Kingmaker',
    engine: 'TITAN',
    summary: 'Supports focused founders with early capital and strategic pattern reading.',
    sectors: ['Climate', 'SaaS', 'Infrastructure'],
    stages: ['Seed', 'Series A'],
    checkSize: '$50k to $250k',
    thesis: 'Climate infrastructure, SaaS, and founders who can manage operational pressure.',
    style: 'Selective capital and board support',
    access: 'Structured request',
    verified: true,
    angel: true,
    tone: 'portrait-four',
    surfaced: ['Useful for climate ventures with serious execution needs.', 'Best for founders with clear traction signals.'],
  },
  {
    id: 'nadia-owen',
    type: 'angel',
    name: 'Nadia Owen',
    title: 'Product Angel',
    arc: 'Composer',
    engine: 'ARTISAN',
    summary: 'Invests in product led teams with strong user insight and clear founder discipline.',
    sectors: ['Product tools', 'Consumer software', 'AI'],
    stages: ['Pre seed', 'Seed'],
    checkSize: '$15k to $75k',
    thesis: 'Product led software where user insight and founder discipline are visible.',
    style: 'Product review and early checks',
    access: 'Warm request required',
    verified: true,
    angel: true,
    tone: 'portrait-one',
    surfaced: ['Relevant for product led software ventures.', 'Good match when the founder can show user pull.'],
  },
];

const articles = [
  {
    id: 'arc-gap',
    type: 'article',
    title: 'Why founder gaps show up before the company breaks',
    category: 'Founder teams',
    summary: 'A practical note on spotting missing capability before it becomes a hiring mistake, cofounder conflict, or stalled raise.',
    author: 'MYARC Editorial',
    readTime: '4 min read',
    tags: ['Cofounders', 'Team design', 'Arc data'],
  },
  {
    id: 'angel-context',
    type: 'article',
    title: 'Angel attention is earned through context',
    category: 'Capital',
    summary: 'How structured requests can help serious founders reach angels without turning the platform into another noisy inbox.',
    author: 'MYARC Editorial',
    readTime: '3 min read',
    tags: ['Angels', 'Funding', 'Requests'],
  },
  {
    id: 'profile-signal',
    type: 'article',
    title: 'The profile after the Brain Scan',
    category: 'Onboarding',
    summary: 'How MYARC should collect skills, venture details, intent, and LinkedIn context after the Arc profile is created.',
    author: 'MYARC Product',
    readTime: '5 min read',
    tags: ['Profile setup', 'LinkedIn', 'Network'],
  },
];

const profile = {
  name: 'Bathiya',
  arc: 'Kingmaker',
  engine: 'TITAN',
  headline: 'Founder building MYARC Constellation and Life OS.',
  intent: 'Looking for technical cofounder, product talent, and aligned angels.',
  availability: 'Founder mode',
  accountSource: 'Created from Brain Scan account',
  verified: true,
  strengths: ['Finance background', 'Product vision', 'Community building', 'High pressure execution'],
  visibleSignals: ['Brain Scan Arc', 'Selected Life OS proof', 'Founder asks'],
  onboardingSteps: ['Brain Scan complete', 'Guided profile setup pending', 'LinkedIn enrichment later', 'Manual verification requested'],
};

const ENGINE_CLASSES = new Set(['TITAN', 'ARBITER', 'ALCHEMIST', 'ARTISAN', 'ORACLE', 'SOVEREIGN']);

function getEngineClass(engine) {
  const key = String(engine || '').toUpperCase();
  return ENGINE_CLASSES.has(key) ? key : 'DEFAULT';
}

function ArcTag({ arc, engine }) {
  const description = arcDescriptions[arc] || [
    'Shows how this person tends to think, build, and decide.',
    'Useful for understanding where they create the most value.',
    'Best read alongside skills, intent, and real work signals.',
  ];

  return (
    <button
      type="button"
      className={`network-arc-tag ${getEngineClass(engine)}`}
      aria-label={`${arc} Arc description`}
      onClick={(event) => event.stopPropagation()}
    >
      <span>{arc}</span>
      <span className="network-arc-info" aria-hidden="true">
        i
      </span>
      <span className="network-arc-tooltip" role="tooltip">
        <strong>{arc}</strong>
        {description.map((line) => <span key={line}>{line}</span>)}
      </span>
    </button>
  );
}

function VerifiedBadge({ engine = 'DEFAULT' }) {
  return (
    <span className={`network-verified-badge ${getEngineClass(engine)}`} title="MYARC verified" aria-label="MYARC verified">
      <Check size={12} strokeWidth={3} />
    </span>
  );
}

function AngelBadge() {
  return (
    <span className="network-angel-badge" title="MYARC angel" aria-label="MYARC angel">
      A
    </span>
  );
}

function NameLine({ name, arc, engine, verified = false, angel = false }) {
  return (
    <span className="network-name-line">
      <span>{name}</span>
      {verified && <VerifiedBadge engine={angel ? 'DEFAULT' : engine} />}
      {angel && <AngelBadge />}
      {arc && <ArcTag arc={arc} engine={engine} />}
    </span>
  );
}

function VentureImage({ tone, label }) {
  return (
    <div className={`network-mock-image ${tone}`}>
      {tone === 'mountain' && <strong>NEXORA</strong>}
      {tone === 'markets' && <span className="network-signal-mark">S</span>}
      {tone === 'objects' && <span className="network-object-mark" />}
      {tone === 'grid' && <strong>HELIO</strong>}
      {tone === 'angel' && <strong>{label}</strong>}
    </div>
  );
}

function PersonPortrait({ tone, name }) {
  return (
    <div className={`network-portrait ${tone}`}>
      <span>{name.charAt(0)}</span>
    </div>
  );
}

function stopAndRun(event, action) {
  event.stopPropagation();
  action();
}

function PremiumButton({ children, onClick, variant = 'ghost' }) {
  return (
    <button type="button" className={`network-action-button ${variant}`} onClick={onClick}>
      {children}
    </button>
  );
}

function getRequestLabel(item) {
  if (item?.type === 'venture') return 'Contact founder';
  if (item?.type === 'angel') return 'Request access';
  return 'Request connection';
}

function getRequestStatus(item) {
  if (item?.type === 'venture') return 'Founder contacted';
  if (item?.type === 'angel') return 'Access requested';
  return 'Connection requested';
}

function VentureCard({ venture, active, saved, onOpen, onSave, onRequest }) {
  return (
    <article
      className={`network-mock-card network-venture-card ${active ? 'active' : ''}`}
      onClick={(event) => stopAndRun(event, onOpen)}
    >
      <VentureImage tone={venture.imageTone} label={venture.title} />
      <button type="button" className={`network-card-save ${saved ? 'saved' : ''}`} onClick={(event) => stopAndRun(event, onSave)} aria-label={`Save ${venture.title}`}>
        <Bookmark size={17} />
      </button>
      <div className="network-card-copy">
        <div className="network-card-signal">
          <span>{venture.matchSignal}</span>
          <strong>{venture.capital}</strong>
        </div>
        <h3>{venture.title}</h3>
        <p>{venture.summary}</p>
        <div className="network-card-fields two">
          <span><small>Stage</small>{venture.stage}</span>
          <span><small>Sector</small>{venture.sector}</span>
        </div>
        <div className="network-card-fields">
          <span><small>Next gap</small>{venture.gapSignal}</span>
        </div>
        <div className="network-founder-strip">
          <PersonPortrait tone="portrait-two" name={venture.founder} />
          <div>
            <strong><NameLine name={venture.founder} arc={venture.founderArc} engine={venture.founderEngine} verified={venture.founderVerified} /></strong>
            <span>{venture.founderRole}</span>
          </div>
        </div>
      </div>
      <div className="network-card-actions">
        <button type="button" onClick={(event) => stopAndRun(event, onOpen)}>View</button>
        <button type="button" onClick={(event) => stopAndRun(event, onSave)}>{saved ? 'Saved' : 'Save'}</button>
        <button type="button" onClick={(event) => stopAndRun(event, onRequest)}><Send size={13} /> Contact founder</button>
      </div>
    </article>
  );
}

function PersonCard({ person, active, saved, onOpen, onSave, onRequest }) {
  return (
    <article
      className={`network-mock-card network-person-card ${active ? 'active' : ''}`}
      onClick={(event) => stopAndRun(event, onOpen)}
    >
      <div className="network-person-image">
        <PersonPortrait tone={person.tone} name={person.name} />
      </div>
      <button type="button" className={`network-card-save ${saved ? 'saved' : ''}`} onClick={(event) => stopAndRun(event, onSave)} aria-label={`Save ${person.name}`}>
        <Bookmark size={17} />
      </button>
      <div className="network-card-copy">
        <h3><NameLine name={person.name} arc={person.arc} engine={person.engine} verified={person.verified} /></h3>
        <strong className="network-person-title">{person.title}</strong>
        <p>{person.summary}</p>
        <div className="network-card-signal small">
          <span>{person.availability}</span>
          <strong>{person.credibility}</strong>
        </div>
        <div className="network-chip-row">
          {person.tags.map((tag) => <span key={tag}>{tag}</span>)}
        </div>
      </div>
      <div className="network-card-actions single">
        <button type="button" onClick={(event) => stopAndRun(event, onRequest)}><Send size={13} /> Request connection</button>
      </div>
    </article>
  );
}

function AngelCard({ angel, active, saved, onOpen, onSave, onRequest }) {
  return (
    <article
      className={`network-mock-card network-angel-card ${active ? 'active' : ''}`}
      onClick={(event) => stopAndRun(event, onOpen)}
    >
      <div className="network-person-image angel">
        <PersonPortrait tone={angel.tone} name={angel.name} />
      </div>
      <button type="button" className={`network-card-save ${saved ? 'saved' : ''}`} onClick={(event) => stopAndRun(event, onSave)} aria-label={`Save ${angel.name}`}>
        <Bookmark size={17} />
      </button>
      <div className="network-card-copy">
        <h3><NameLine name={angel.name} arc={angel.arc} engine={angel.engine} verified={angel.verified} angel /></h3>
        <strong className="network-person-title">{angel.title}</strong>
        <p>{angel.summary}</p>
        <div className="network-card-signal small">
          <span>{angel.checkSize}</span>
          <strong>{angel.stages.join(', ')}</strong>
        </div>
        <div className="network-chip-row">
          {angel.sectors.slice(0, 3).map((sector) => <span key={sector}>{sector}</span>)}
        </div>
      </div>
      <div className="network-card-actions single">
        <button type="button" onClick={(event) => stopAndRun(event, onRequest)}><LockKeyhole size={13} /> Request access</button>
      </div>
    </article>
  );
}

function ArticleCard({ article, onOpen }) {
  return (
    <article className="network-article-card" onClick={(event) => stopAndRun(event, onOpen)}>
      <span>{article.category}</span>
      <h3>{article.title}</h3>
      <p>{article.summary}</p>
      <div className="network-article-meta">
        <strong>{article.author}</strong>
        <small>{article.readTime}</small>
      </div>
      <div className="network-chip-row">
        {article.tags.map((tag) => <span key={tag}>{tag}</span>)}
      </div>
    </article>
  );
}

function MessageCard({ message, onOpen }) {
  return (
    <article className="network-message-card" onClick={(event) => stopAndRun(event, onOpen)}>
      <span className={`network-message-status ${message.status.toLowerCase().replaceAll(' ', '-')}`}>{message.status}</span>
      <div>
        <h3>{message.targetName}</h3>
        <p>{message.note}</p>
      </div>
      <small>{message.createdAt}</small>
    </article>
  );
}

function AccessPanel({ onRequest }) {
  return (
    <article className="network-access-panel">
      <div>
        <h3>
          Angels
          <span><LockKeyhole size={13} /> Structured access</span>
        </h3>
        <p>Angel access is private and structured so useful requests rise above noise.</p>
      </div>
      <div className="network-access-footer">
        <div className="network-avatar-stack">
          {people.slice(0, 4).map((person) => (
            <PersonPortrait key={person.id} tone={person.tone} name={person.name} />
          ))}
          <span>+12</span>
        </div>
        <button type="button" onClick={onRequest}>Request access</button>
      </div>
    </article>
  );
}

function RequestModal({ target, onClose, onSubmit }) {
  const [note, setNote] = useState('');
  if (!target) return null;

  const title = target.title || target.name;
  const requestLabel = getRequestLabel(target);
  const contextItems = target.type === 'venture'
    ? [target.stage, target.capital, target.needs?.[0]]
    : target.type === 'angel'
      ? [target.checkSize, target.stages?.join(', '), target.sectors?.[0]]
      : [target.arc, target.availability, target.tags?.[0]];

  return (
    <div className="network-modal-backdrop" onClick={onClose}>
      <section className="network-request-modal" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="network-modal-close" onClick={onClose} aria-label="Close request"><X size={17} /></button>
        <span className="network-modal-label">{requestLabel}</span>
        <h2>{title}</h2>
        <p>Send a short, useful note. The other side can review the context before a conversation opens.</p>
        <div className="network-request-context">
          {contextItems.filter(Boolean).map((item) => <span key={item}>{item}</span>)}
        </div>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What are you asking for, and why does this conversation make sense?"
        />
        <div className="network-modal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" onClick={() => onSubmit(note || `I would like to connect with ${title}.`)}><Send size={14} /> Send</button>
        </div>
      </section>
    </div>
  );
}

function DetailDrawer({ selected, savedIds, onClose, onSave, onRequest, onOpenPerson, onToast }) {
  const item = selected?.item;

  if (!item) {
    return (
      <aside className="network-dossier-panel" aria-hidden="true" />
    );
  }

  const isVenture = item.type === 'venture';
  const isPerson = item.type === 'person';
  const isAngel = item.type === 'angel';
  const title = item.title || item.name;

  return (
    <aside
      className="network-dossier-panel open"
      onClick={(event) => event.stopPropagation()}
      aria-hidden="false"
    >
      <button type="button" className="network-panel-close" aria-label="Close preview" onClick={onClose}>x</button>
      {isVenture ? (
        <VentureImage tone={item.imageTone} label={item.title} />
      ) : (
        <div className="network-drawer-person-hero">
          <PersonPortrait tone={item.tone || 'portrait-two'} name={title} />
          <span>{isAngel ? 'Angel profile' : 'Constellation profile'}</span>
        </div>
      )}

      <div className="network-dossier-head">
        <div className="network-dossier-avatar">{title.charAt(0)}</div>
        <div>
          <h2>
            <NameLine
              name={title}
              arc={(isPerson || isAngel) ? item.arc : null}
              engine={item.engine || 'DEFAULT'}
              verified={(isPerson && item.verified) || isAngel}
              angel={isAngel}
            />
          </h2>
          <p>{item.summary}</p>
        </div>
      </div>

      <div className="network-dossier-actions">
        {isVenture && <button type="button" onClick={() => onToast(`${title} details are open.`)}>View details</button>}
        {isPerson && <button type="button" onClick={() => onToast(`${title} profile is open.`)}>View profile</button>}
        {isAngel && <button type="button" onClick={() => onRequest(item)}><LockKeyhole size={13} /> Request access</button>}
        <button type="button" onClick={() => onSave(item)}>{savedIds.includes(item.id) ? 'Saved' : 'Save'}</button>
        <button type="button" onClick={() => onRequest(item)}><Send size={13} /> {getRequestLabel(item)}</button>
      </div>

      {isVenture && (
        <>
          <div className="network-dossier-meta">
            <span><small>Stage</small>{item.stage}</span>
            <span><small>Sector</small>{item.sector}</span>
            <span><small>Location</small>{item.location}</span>
          </div>

          <div className="network-dossier-highlight">
            <span>{item.matchSignal}</span>
            <strong>{item.gapSignal}</strong>
            <p>Full page can include: {item.dataRoom}.</p>
          </div>

          <section>
            <h3>Why shown</h3>
            {item.surfaced.map((line) => <p key={line}>{line}</p>)}
          </section>

          <section>
            <h3>Problem</h3>
            <p>{item.problem}</p>
          </section>

          <section>
            <h3>Solution</h3>
            <p>{item.solution}</p>
          </section>

          <section>
            <h3>Looking for</h3>
            <div className="network-chip-row">
              {item.needs.map((need) => <span key={need}>{need}</span>)}
            </div>
          </section>

          <section>
            <h3>Progress</h3>
            <div className="network-proof-list">
              {item.traction.map((point) => <span key={point}>{point}</span>)}
            </div>
          </section>

          <section>
            <div className="network-section-line">
              <h3>Current needs</h3>
              <button type="button" onClick={() => onRequest(item)}>Contact founder</button>
            </div>
            <div className="network-role-list">
              {item.openRoles.map((role) => (
                <article key={role}>
                  <BadgeCheck size={16} />
                  <div>
                    <strong>{role}</strong>
                    <span>{item.stage} need</span>
                  </div>
                  <button type="button" onClick={() => onRequest(item)}>Ask</button>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3>Founder</h3>
            <div className="network-founder-strip dossier">
              <PersonPortrait tone="portrait-two" name={item.founder} />
              <div>
                <strong><NameLine name={item.founder} arc={item.founderArc} engine={item.founderEngine} verified={item.founderVerified} /></strong>
                <span>{item.founderRole}</span>
                <p>{item.founderBio}</p>
              </div>
              <button type="button" onClick={() => onOpenPerson(item.founder)}>View profile</button>
            </div>
          </section>

          <section>
            <h3>Full venture page</h3>
            <p>The owner controls what appears here. MYARC keeps the page focused on the founder, product, progress, funding context, and current needs.</p>
          </section>
        </>
      )}

      {isPerson && (
        <>
          <div className="network-dossier-meta">
            <span><small>Arc</small>{item.arc}</span>
            <span><small>Availability</small>{item.availability}</span>
            <span><small>Location</small>{item.location}</span>
          </div>

          <section>
            <h3>Intent</h3>
            <p>{item.intent}</p>
            <p>{item.networkAsk}</p>
          </section>

          <section>
            <h3>Strengths</h3>
            <div className="network-proof-list">
              {item.strengths.map((point) => <span key={point}>{point}</span>)}
            </div>
          </section>

          <section>
            <h3>Why this surfaced</h3>
            {item.surfaced.map((line) => <p key={line}>{line}</p>)}
          </section>
        </>
      )}

      {isAngel && (
        <>
          <div className="network-dossier-meta">
            <span><small>Stages</small>{item.stages.join(', ')}</span>
            <span><small>Arc</small>{item.arc}</span>
            <span><small>Style</small>{item.style}</span>
          </div>

          <div className="network-dossier-highlight">
            <span>{item.checkSize}</span>
            <strong>{item.thesis}</strong>
            <p>Angel conversations open after a structured request is accepted.</p>
          </div>

          <section>
            <h3>Sectors</h3>
            <div className="network-chip-row">
              {item.sectors.map((sector) => <span key={sector}>{sector}</span>)}
            </div>
          </section>

          <section>
            <h3>Why this surfaced</h3>
            {item.surfaced.map((line) => <p key={line}>{line}</p>)}
          </section>

          <section>
            <h3>Access path</h3>
            <p>Angel conversations open after a structured request is accepted. This keeps attention clean while making useful conversations possible.</p>
          </section>
        </>
      )}
    </aside>
  );
}

function ProfileSurface({ onVerify }) {
  return (
    <section className="network-profile-surface">
      <div className="network-profile-card">
        <PersonPortrait tone="portrait-two" name={profile.name} />
        <div>
          <span className="network-modal-label">Public Constellation profile</span>
          <h2><NameLine name={profile.name} arc={profile.arc} engine={profile.engine} verified={profile.verified} /></h2>
          <p>{profile.headline}</p>
        </div>
      </div>

      <div className="network-profile-grid">
        <article>
          <h3>Account</h3>
          <p>{profile.accountSource}</p>
        </article>
        <article>
          <h3>Current intent</h3>
          <p>{profile.intent}</p>
        </article>
        <article>
          <h3>Availability</h3>
          <p>{profile.availability}</p>
        </article>
        <article>
          <h3>Visible signals</h3>
          <div className="network-chip-row">
            {profile.visibleSignals.map((signal) => <span key={signal}>{signal}</span>)}
          </div>
        </article>
        <article className="network-profile-wide">
          <h3>Network onboarding</h3>
          <p>After Brain Scan, the live platform should guide users through a short AI-style setup instead of a long form.</p>
          <div className="network-onboarding-track">
            {profile.onboardingSteps.map((step, index) => (
              <span key={step} className={index === 0 ? 'complete' : ''}>
                <BadgeCheck size={13} />
                {step}
              </span>
            ))}
          </div>
        </article>
        <article>
          <h3>Strengths</h3>
          <div className="network-chip-row">
            {profile.strengths.map((strength) => <span key={strength}>{strength}</span>)}
          </div>
        </article>
        <article>
          <h3>Verification</h3>
          <p>First 100 profiles can receive manual MYARC verification free after review. Later verification can become part of a paid tier.</p>
          <button type="button" className="network-action-button gold" onClick={onVerify}>Request verification</button>
        </article>
      </div>
    </section>
  );
}

export default function MyarcNetworkPreview() {
  const [activeTab, setActiveTab] = useState('For You');
  const [activeIntent, setActiveIntent] = useState('Find cofounder');
  const [selected, setSelected] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [requestTarget, setRequestTarget] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 'seed-1',
      targetName: 'Nexora',
      status: 'Draft ready',
      note: 'Founder contact waiting for review.',
      createdAt: 'Today',
    },
  ]);
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const byQuery = (item) => {
      const haystack = [
        item.title,
        item.name,
        item.summary,
        item.capital,
        item.gapSignal,
        item.matchSignal,
        item.dataRoom,
        item.networkAsk,
        item.credibility,
        item.thesis,
        item.checkSize,
        item.sector,
        item.location,
        item.stage,
        ...(item.needs || []),
        ...(item.tags || []),
        ...(item.sectors || []),
        ...(item.openRoles || []),
        ...(item.qualitySignals || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return !query || haystack.includes(query);
    };
    if (activeTab !== 'For You') {
      return {
        ventures: ventures.filter(byQuery),
        people: people.filter(byQuery),
        angels: angels.filter(byQuery),
        articles: articles.filter(byQuery),
      };
    }

    const byIntent = (item) => item.intents?.includes(activeIntent);

    if (activeIntent === 'Find cofounder') {
      return {
        ventures: [],
        people: people.filter((item) => byQuery(item) && byIntent(item)),
        angels: [],
        articles: [],
      };
    }

    if (activeIntent === 'Find talent') {
      return {
        ventures: [],
        people: people.filter((item) => byQuery(item) && byIntent(item)),
        angels: [],
        articles: [],
      };
    }

    if (activeIntent === 'Find work') {
      return {
        ventures: ventures.filter((item) => byQuery(item) && byIntent(item)),
        people: [],
        angels: [],
        articles: [],
      };
    }

    if (activeIntent === 'Find funding') {
      return {
        ventures: [],
        people: [],
        angels: angels.filter(byQuery),
        articles: [],
      };
    }

    if (activeIntent === 'Invest in ventures') {
      return {
        ventures: ventures.filter((item) => byQuery(item) && byIntent(item)),
        people: [],
        angels: [],
        articles: [],
      };
    }

    return {
      ventures: [],
      people: [],
      angels: [],
      articles: [],
    };
  }, [activeIntent, activeTab, search]);

  const activeItem = selected?.item;
  const tabTitle = useMemo(() => {
    if (activeTab === 'Messages') return 'Private request inbox';
    if (activeTab === 'Profile') return 'Constellation profile';
    if (activeTab === 'Articles') return 'Articles';
    if (activeTab !== 'For You') return activeTab;
    return 'Curated for you';
  }, [activeTab]);

  const subtitle = activeTab === 'For You'
    ? intentCopy[activeIntent]
    : activeTab === 'Profile'
      ? 'Your public MYARC Constellation identity, Arc signal, asks, and availability.'
      : activeTab === 'Articles'
        ? 'MYARC-owned notes, product updates, and founder essays. This is curated by the founder, not a public feed.'
        : 'A focused surface for the current product area.';

  const openItem = (item) => setSelected({ type: item.type, item });

  const toggleSave = (item) => {
    setSavedIds((current) => current.includes(item.id)
      ? current.filter((id) => id !== item.id)
      : [...current, item.id]);
    setToast(savedIds.includes(item.id) ? `Removed ${item.title || item.name} from saved.` : `Saved ${item.title || item.name}.`);
  };

  const submitRequest = (note) => {
    if (!requestTarget) return;
    const title = requestTarget.title || requestTarget.name;
    setMessages((current) => [
      {
        id: `${requestTarget.id}-${Date.now()}`,
        targetName: title,
        status: getRequestStatus(requestTarget),
        note,
        createdAt: 'Just now',
      },
      ...current,
    ]);
    setToast(`Request sent for ${title}.`);
    setRequestTarget(null);
    setActiveTab('Messages');
  };

  const openFounderProfile = (founderName) => {
    const person = people.find((item) => item.name === founderName) || {
      id: `founder-${founderName}`,
      type: 'person',
      name: founderName,
      arc: activeItem?.founderArc || 'Founder',
      engine: activeItem?.founderEngine || 'DEFAULT',
      title: activeItem?.founderRole || 'Founder',
      summary: activeItem?.founderBio || 'Founder profile preview.',
      intent: 'Building the selected venture.',
      availability: 'Request connection',
      verified: Boolean(activeItem?.founderVerified),
      location: activeItem?.location || 'Global',
      tags: ['Founder', activeItem?.sector || 'Venture'],
      strengths: ['Venture leadership', 'Clear current ask'],
      surfaced: ['Connected to the selected venture.'],
      tone: 'portrait-two',
    };
    openItem(person);
  };

  const renderCards = () => {
    if (activeTab === 'Profile') return <ProfileSurface onVerify={() => setToast('Verification request saved for review.')} />;

    if (activeTab === 'Messages') {
      return (
        <div className="network-message-list">
          {messages.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              onOpen={() => {
                setToast(`Opened request for ${message.targetName}.`);
              }}
            />
          ))}
        </div>
      );
    }

    if (activeTab === 'Articles') {
      return (
        <div className="network-article-grid">
          {filtered.articles.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              onOpen={() => setToast(`${article.title} opened.`)}
            />
          ))}
        </div>
      );
    }

    if (activeTab === 'Angels') {
      return (
        <div className="network-card-grid people-row">
          {filtered.angels.map((angel) => (
            <AngelCard
              key={angel.id}
              angel={angel}
              active={activeItem?.id === angel.id}
              saved={savedIds.includes(angel.id)}
              onOpen={() => openItem(angel)}
              onSave={() => toggleSave(angel)}
              onRequest={() => setRequestTarget(angel)}
            />
          ))}
        </div>
      );
    }

    if (activeTab === 'People') {
      return (
        <div className="network-card-grid people-row">
          {filtered.people.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              active={activeItem?.id === person.id}
              saved={savedIds.includes(person.id)}
              onOpen={() => openItem(person)}
              onSave={() => toggleSave(person)}
              onRequest={() => setRequestTarget(person)}
            />
          ))}
        </div>
      );
    }

    if (activeTab === 'Ventures') {
      return (
        <div className="network-card-grid primary">
          {filtered.ventures.map((venture) => (
            <VentureCard
              key={venture.id}
              venture={venture}
              active={activeItem?.id === venture.id}
              saved={savedIds.includes(venture.id)}
              onOpen={() => openItem(venture)}
              onSave={() => toggleSave(venture)}
              onRequest={() => setRequestTarget(venture)}
            />
          ))}
        </div>
      );
    }

    return (
      <>
        {filtered.ventures.length > 0 && (
          <div className="network-card-grid primary">
            {filtered.ventures.map((venture) => (
              <VentureCard
                key={venture.id}
                venture={venture}
                active={activeItem?.id === venture.id}
                saved={savedIds.includes(venture.id)}
                onOpen={() => openItem(venture)}
                onSave={() => toggleSave(venture)}
                onRequest={() => setRequestTarget(venture)}
              />
            ))}
          </div>
        )}

        {filtered.people.length > 0 && (
          <>
            <div className="network-section-heading compact">
              <div>
                <h2>People matched to this intent</h2>
                <p>Founders and skilled talent shaped by Arc data, skills, and intent.</p>
              </div>
              <button type="button" onClick={() => setActiveTab('People')}>See all</button>
            </div>

            <div className="network-card-grid people-row">
              {filtered.people.map((person) => (
                <PersonCard
                  key={person.id}
                  person={person}
                  active={activeItem?.id === person.id}
                  saved={savedIds.includes(person.id)}
                  onOpen={() => openItem(person)}
                  onSave={() => toggleSave(person)}
                  onRequest={() => setRequestTarget(person)}
                />
              ))}
            </div>
          </>
        )}

        {filtered.angels.length > 0 && (
          <>
            <AccessPanel onRequest={() => {
              setActiveTab('Angels');
              setRequestTarget(angels[0]);
            }} />

            <div className="network-card-grid people-row">
              {filtered.angels.map((angel) => (
                <AngelCard
                  key={angel.id}
                  angel={angel}
                  active={activeItem?.id === angel.id}
                  saved={savedIds.includes(angel.id)}
                  onOpen={() => openItem(angel)}
                  onSave={() => toggleSave(angel)}
                  onRequest={() => setRequestTarget(angel)}
                />
              ))}
            </div>
          </>
        )}

        <div className="network-news-strip">
          <Sparkles size={17} />
          <div>
            <strong>Network intelligence</strong>
            <span>Every card should lead to a useful conversation, a venture detail page, or a saved opportunity.</span>
          </div>
        </div>
      </>
    );
  };

  const visibleCardCount = activeTab === 'Ventures'
    ? filtered.ventures.length
    : activeTab === 'People'
      ? filtered.people.length
      : activeTab === 'Angels'
        ? filtered.angels.length
        : activeTab === 'Articles'
          ? filtered.articles.length
          : filtered.ventures.length + filtered.people.length + filtered.angels.length;

  const hasCards = activeTab === 'Profile'
    || activeTab === 'Messages'
    || visibleCardCount > 0;

  return (
    <div className="network-preview-shell font-body">
      <div className="network-preview-glow" />
      <header className="network-preview-nav">
        <Link to="/" className="network-preview-brand">
          <img src="/assets/logo.png" alt="MYARC" />
          <span>MYARC Constellation</span>
        </Link>

        <nav aria-label="MYARC Constellation preview tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={activeTab === tab ? 'active' : ''}
              onClick={() => {
                setActiveTab(tab);
                setSelected(null);
              }}
            >
              {tab}
            </button>
          ))}
        </nav>

        <div className="network-preview-nav-actions">
          <label className="network-search-box">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search ventures, people, angels..."
            />
          </label>
          <button type="button" aria-label="Notifications" onClick={() => setToast('No new platform notifications yet.')}><Bell size={17} /></button>
          <button type="button" className="network-profile-pill" aria-label="Profile" onClick={() => setActiveTab('Profile')}>
            <PersonPortrait tone="portrait-two" name="Bathiya" />
            <span>Bathiya</span>
          </button>
        </div>
      </header>

      <main className="network-preview-main" onClick={() => setSelected(null)}>
        {activeTab === 'For You' && (
          <section className="network-intent-bar" aria-label="Network intent filters">
            {intentItems.map(({ label, icon: Icon }) => (
              <button
                type="button"
                className={activeIntent === label ? 'active' : ''}
                key={label}
                onClick={(event) => stopAndRun(event, () => setActiveIntent(label))}
              >
                <Icon size={17} />
                {label}
              </button>
            ))}
          </section>
        )}

        <div className="network-preview-content">
          <section className="network-dealroom-main">
            <div className="network-section-heading">
              <div>
                <h2>{tabTitle}</h2>
                <p>{subtitle}</p>
              </div>
              <button type="button" onClick={(event) => stopAndRun(event, () => setToast(`${tabTitle} refreshed.`))}>Refresh</button>
            </div>

            {hasCards ? renderCards() : (
              <div className="network-empty-state">
                <Search size={24} />
                <h3>No matches yet</h3>
                <p>Try another intent or clear the search to bring the card surface back.</p>
                <PremiumButton onClick={() => setSearch('')} variant="gold">Clear search</PremiumButton>
              </div>
            )}
          </section>

          <DetailDrawer
            selected={selected}
            savedIds={savedIds}
            onClose={() => setSelected(null)}
            onSave={toggleSave}
            onRequest={setRequestTarget}
            onOpenPerson={openFounderProfile}
            onToast={setToast}
          />
        </div>
      </main>

      <RequestModal
        target={requestTarget}
        onClose={() => setRequestTarget(null)}
        onSubmit={submitRequest}
      />

      {toast && (
        <button type="button" className="network-toast" onClick={() => setToast('')}>
          {toast}
        </button>
      )}
    </div>
  );
}
